import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { displayDate, displayDateTime } from '../common/datePresentation';
import DateField from '../common/DateField';
import formatPayrollAmount from './teacherSettlementPresentation';
import TeacherSettlementGroups from './TeacherSettlementLessonGroups';

export default function TeacherSettlementDetails({ report, ready, personal, service, runOperation, command, t, language, ...groupProps }) {
  const formatLedgerAmount = value => formatPayrollAmount(value, language);
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [rateInfo, setRateInfo] = useState(null);
  const [rate, setRate] = useState({ type: 'per_lesson', amount: '', valid_from: `${report.month}-01`, valid_to: '' });
  const submitCommand = async input => {
    if (await command(report, input)) { setNote(''); setAmount(''); }
  };
  const loadRates = async () => {
    const result = await runOperation(() => service.getRates(report.teacherId), false);
    if (result) setRateInfo(result);
  };
  const saveRate = async event => {
    event.preventDefault();
    const result = await runOperation(() => service.saveRate(report.teacherId, { ...rate, amount: Number(rate.amount) }, rateInfo.revision));
    if (result) setRateInfo(null);
  };
  return (
    <div>
      <TeacherSettlementGroups report={report} t={t} language={language} {...groupProps} />
      {(report.lines || []).filter(line => line.kind === 'fixed').map(line => <p key={line.id}>{t('fixed')}: {formatLedgerAmount(line.amount)}</p>)}
      {(report.corrections || []).map(item => <p key={item.id}>{t('correction')}: {formatLedgerAmount(item.amount)} — {item.note}</p>)}
      <details className="payroll-adjustments">
        <summary><i className="far fa-flag" aria-hidden="true" /> {t(personal ? 'issuePrompt' : 'adjustmentsTitle')}</summary>
        <div className="payroll-adjustments-body">
          {(report.audit || []).length > 0 && <ul>{report.audit.map(item => <li key={item.revision}>{displayDateTime(item.at, language)} — {item.actorId}: {t(item.action)} {item.note}</li>)}</ul>}
          <label htmlFor={`payroll-note-${report.teacherId}`}>{t('note')}</label>
          <textarea id={`payroll-note-${report.teacherId}`} className="form-control mb-2" value={note} maxLength={1000} onChange={event => setNote(event.target.value)} />
          {personal ? (
            <button
              type="button"
              className="btn manage-red-button mr-2"
              disabled={!ready || !note.trim() || report.status !== 'teacher_verification'}
              onClick={() => submitCommand({ action: 'reportIssue', note })}
            >{t('reportIssue')}
            </button>
          ) : (
            <div>
              <label htmlFor={`payroll-correction-${report.teacherId}`}>{t('correctionAmount')}</label>
              <input id={`payroll-correction-${report.teacherId}`} type="number" step="0.01" className="form-control mb-2" value={amount} onChange={event => setAmount(event.target.value)} />
              <button
                type="button"
                className="btn manage-red-button mr-2 mb-2"
                disabled={!ready || !note.trim() || amount === '' || !Number.isFinite(Number(amount))}
                onClick={() => submitCommand({ action: 'correction', amount: Number(amount), note })}
              >{t('addCorrection')}
              </button>
              {report.status !== 'approved' && (
              <button
                type="button"
                className="btn manage-teal-button mr-2 mb-2"
                disabled={!ready || !note.trim()}
                onClick={() => submitCommand({ action: 'recalculate', note })}
              >{t('recalculate')}
              </button>
              )}
              {report.issue && (
              <button
                type="button"
                className="btn manage-red-button mr-2"
                disabled={!ready || !note.trim()}
                onClick={() => submitCommand({ action: 'resolveIssue', note })}
              >{t('resolveIssue')}
              </button>
              )}
              <button type="button" className="btn manage-teal-button mb-2" disabled={!ready} onClick={loadRates}>{t('rates')}</button>
              {rateInfo && (
              <form onSubmit={saveRate}>
                <p>{t('rateHelp')}</p>
                {rateInfo.versions.map(item => (
                  <p key={item.valid_from}>{displayDate(item.valid_from, language)} — {displayDate(item.valid_to, language) || '∞'}: {t(item.type)} {formatLedgerAmount(item.amount)}</p>
                ))}
                <label htmlFor={`payroll-rate-type-${report.teacherId}`}>{t('rateType')}</label>
                <select id={`payroll-rate-type-${report.teacherId}`} className="form-control" value={rate.type} onChange={event => setRate({ ...rate, type: event.target.value })}>
                  {['hourly', 'per_lesson', 'fixed'].map(type => <option key={type} value={type}>{t(type)}</option>)}
                </select>
                <label htmlFor={`payroll-rate-amount-${report.teacherId}`}>{t('amount')}</label>
                <input
                  id={`payroll-rate-amount-${report.teacherId}`}
                  className="form-control"
                  type="number"
                  min="0"
                  max="1000000"
                  step="0.01"
                  required
                  value={rate.amount}
                  onChange={event => setRate({ ...rate, amount: event.target.value })}
                />
                <label htmlFor={`payroll-valid-from-${report.teacherId}`}>{t('validFrom')}</label>
                <DateField id={`payroll-valid-from-${report.teacherId}`} value={rate.valid_from} required onChange={event => setRate({ ...rate, valid_from: event.target.value })} />
                <label htmlFor={`payroll-valid-to-${report.teacherId}`}>{t('validTo')}</label>
                <DateField id={`payroll-valid-to-${report.teacherId}`} value={rate.valid_to} onChange={event => setRate({ ...rate, valid_to: event.target.value })} />
                <button type="submit" className="btn manage-red-button mt-2 mb-3" disabled={!ready}>{t('saveRate')}</button>
              </form>
              )}
            </div>
          )}

        </div>
      </details>
    </div>
  );
}
TeacherSettlementDetails.propTypes = { report: PropTypes.object.isRequired,
  ready: PropTypes.bool.isRequired,
  personal: PropTypes.bool.isRequired,
  service: PropTypes.object.isRequired,
  runOperation: PropTypes.func.isRequired,
  command: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired };
